import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography,
  Chip
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import { Formik } from 'formik';
import * as Yup from 'yup';
import affiliateService from '../../services/affiliateService';

const PerfilAfiliadoEdit = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await affiliateService.getAffiliateProfileMe(auth.token);

      if (response && response.data) {
        setProfile(response.data);
        setIsEditing(true);
      }
    } catch (error) {
      // Perfil não existe ainda
      if (error.response?.status === 404) {
        setProfile(null);
        setIsEditing(false);
      } else {
        console.error(error);
        swal('Erro', 'Erro ao carregar perfil', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleSubmit = async (values, actions) => {
    actions.setSubmitting(true);

    try {
      let response;

      if (isEditing) {
        response = await affiliateService.updateAffiliateProfile(profile.id, values, auth.token);
      } else {
        response = await affiliateService.createAffiliateProfile(values, auth.token);
      }

      if (response && response.data) {
        swal(
          'Sucesso!',
          isEditing
            ? 'Perfil atualizado! Aguarde aprovação do administrador.'
            : 'Perfil cadastrado! Aguarde aprovação do administrador.',
          'success'
        );
        setProfile(response.data);
        setIsEditing(true);
      } else {
        throw new Error('Erro ao salvar perfil');
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao salvar perfil';
      swal('Erro', errorMessage, 'error');
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const initialValues = profile
    ? {
        cpf_cnpj: profile.cpf_cnpj || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        bank_name: profile.bank_name || '',
        bank_code: profile.bank_code || '',
        agency: profile.agency || '',
        account_number: profile.account_number || '',
        account_type: profile.account_type || 'checking',
        pix_type: profile.pix_type || 'cpf',
        pix_key: profile.pix_key || ''
      }
    : {
        cpf_cnpj: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        bank_name: '',
        bank_code: '',
        agency: '',
        account_number: '',
        account_type: 'checking',
        pix_type: 'cpf',
        pix_key: ''
      };

  return (
    <>
      <Helmet>
        <title>Meu Perfil de Afiliado | Sistema de Afiliados</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="lg">
          {profile?.status && (
            <Box sx={{ mb: 3 }}>
              {profile.status === 'pending' && (
                <Alert severity="warning">
                  <Typography variant="body1">
                    Seu perfil está <strong>aguardando aprovação</strong> do administrador.
                  </Typography>
                </Alert>
              )}
              {profile.status === 'approved' && (
                <Alert severity="success">
                  <Typography variant="body1">
                    Seu perfil foi <strong>aprovado</strong>! Você já pode cadastrar seus canais e gerar links.
                  </Typography>
                  {profile.approved_at && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Aprovado em: {new Date(profile.approved_at).toLocaleString('pt-BR')}
                    </Typography>
                  )}
                </Alert>
              )}
              {profile.status === 'rejected' && (
                <Alert severity="error">
                  <Typography variant="body1">
                    Seu perfil foi <strong>rejeitado</strong>.
                  </Typography>
                  {profile.rejection_reason && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Motivo:</strong> {profile.rejection_reason}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Corrija as informações e envie novamente.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              cpf_cnpj: Yup.string().required('CPF/CNPJ é obrigatório').max(18),
              phone: Yup.string().required('Telefone é obrigatório').max(20),
              address: Yup.string(),
              city: Yup.string().max(100),
              state: Yup.string().max(2),
              zip_code: Yup.string().max(10),
              bank_name: Yup.string().max(100),
              bank_code: Yup.string().max(10),
              agency: Yup.string().max(20),
              account_number: Yup.string().max(20),
              account_type: Yup.string().oneOf(['checking', 'savings']),
              pix_type: Yup.string().oneOf(['cpf', 'cnpj', 'email', 'phone', 'random']),
              pix_key: Yup.string().max(100)
            })}
            onSubmit={handleSubmit}
          >
            {({ errors, handleBlur, handleChange, handleSubmit: formikHandleSubmit, isSubmitting, touched, values }) => (
              <form onSubmit={formikHandleSubmit}>
                <Grid container spacing={3}>
                  {/* Dados Pessoais */}
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        title="Dados Pessoais"
                        subheader="Informações básicas do afiliado"
                      />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              error={Boolean(touched.cpf_cnpj && errors.cpf_cnpj)}
                              fullWidth
                              helperText={touched.cpf_cnpj && errors.cpf_cnpj}
                              label="CPF/CNPJ"
                              name="cpf_cnpj"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              required
                              value={values.cpf_cnpj}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              error={Boolean(touched.phone && errors.phone)}
                              fullWidth
                              helperText={touched.phone && errors.phone}
                              label="Telefone"
                              name="phone"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              required
                              value={values.phone}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              error={Boolean(touched.address && errors.address)}
                              fullWidth
                              helperText={touched.address && errors.address}
                              label="Endereço Completo"
                              name="address"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.address}
                              variant="outlined"
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              error={Boolean(touched.city && errors.city)}
                              fullWidth
                              helperText={touched.city && errors.city}
                              label="Cidade"
                              name="city"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.city}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              error={Boolean(touched.state && errors.state)}
                              fullWidth
                              helperText={touched.state && errors.state}
                              label="Estado (UF)"
                              name="state"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.state}
                              variant="outlined"
                              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              error={Boolean(touched.zip_code && errors.zip_code)}
                              fullWidth
                              helperText={touched.zip_code && errors.zip_code}
                              label="CEP"
                              name="zip_code"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.zip_code}
                              variant="outlined"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Dados Bancários */}
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        title="Dados Bancários"
                        subheader="Informações para recebimento de comissões"
                      />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              error={Boolean(touched.bank_name && errors.bank_name)}
                              fullWidth
                              helperText={touched.bank_name && errors.bank_name}
                              label="Nome do Banco"
                              name="bank_name"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.bank_name}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              error={Boolean(touched.bank_code && errors.bank_code)}
                              fullWidth
                              helperText={touched.bank_code && errors.bank_code}
                              label="Código do Banco"
                              name="bank_code"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.bank_code}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              error={Boolean(touched.agency && errors.agency)}
                              fullWidth
                              helperText={touched.agency && errors.agency}
                              label="Agência"
                              name="agency"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.agency}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              error={Boolean(touched.account_number && errors.account_number)}
                              fullWidth
                              helperText={touched.account_number && errors.account_number}
                              label="Número da Conta"
                              name="account_number"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.account_number}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Tipo de Conta</InputLabel>
                              <Select
                                label="Tipo de Conta"
                                name="account_type"
                                value={values.account_type}
                                onChange={handleChange}
                              >
                                <MenuItem value="checking">Conta Corrente</MenuItem>
                                <MenuItem value="savings">Conta Poupança</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Dados PIX */}
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        title="Dados PIX"
                        subheader="Chave PIX para recebimento rápido"
                      />
                      <Divider />
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Tipo de Chave PIX</InputLabel>
                              <Select
                                label="Tipo de Chave PIX"
                                name="pix_type"
                                value={values.pix_type}
                                onChange={handleChange}
                              >
                                <MenuItem value="cpf">CPF</MenuItem>
                                <MenuItem value="cnpj">CNPJ</MenuItem>
                                <MenuItem value="email">E-mail</MenuItem>
                                <MenuItem value="phone">Telefone</MenuItem>
                                <MenuItem value="random">Chave Aleatória</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <TextField
                              error={Boolean(touched.pix_key && errors.pix_key)}
                              fullWidth
                              helperText={touched.pix_key && errors.pix_key}
                              label="Chave PIX"
                              name="pix_key"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values.pix_key}
                              variant="outlined"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Botões */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => navigate('/app/dashboard')}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                      <Button color="primary" variant="contained" type="submit" disabled={isSubmitting}>
                        {(() => {
                          if (isSubmitting) return 'Salvando...';
                          if (isEditing) return 'Atualizar Perfil';
                          return 'Cadastrar Perfil';
                        })()}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  );
};

export default PerfilAfiliadoEdit;
